import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft,
  MessageCircle,
  Phone,
  Heart,
  Shield,
  Brain,
  Sparkles
} from 'lucide-react';

const Experts = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-white/75 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <Link 
            to={isAuthenticated ? "/dashboard" : "/"} 
            className="inline-flex items-center gap-2 text-[#6e6e73] hover:text-[#1d1d1f]"
            style={{transition: 'color 0.2s ease'}}
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">{isAuthenticated ? 'Back to Dashboard' : 'Back to Home'}</span>
          </Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="experts-page">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ background: 'linear-gradient(135deg, #0071e3 0%, #005ec7 100%)' }}>
            <Sparkles className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl font-semibold tracking-tight mb-4" style={{ color: '#1d1d1f', letterSpacing: '-0.025em' }}>Get Personalised EI Guidance</h1>
          <p className="text-lg max-w-xl mx-auto leading-relaxed" style={{ color: '#515154' }}>
            Connect with our emotional intelligence experts instantly via WhatsApp or text message. We're here to help you grow.
          </p>
        </div>

        {/* Contact Cards */}
        <div className="grid sm:grid-cols-2 gap-5 mb-12">
          {/* WhatsApp */}
          <a 
            href="https://wa.me/919717178733?text=Hi, I'd like to learn more about improving my emotional intelligence."
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-contact-btn"
          >
            <Card className="rounded-2xl border-black/5 bg-white card-hover cursor-pointer h-full" style={{transition: 'transform 0.2s ease, box-shadow 0.2s ease'}}>
              <CardContent className="p-8 text-center">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(0,113,227,0.1)' }}>
                  <MessageCircle className="h-7 w-7" style={{ color: '#0071e3' }} />
                </div>
                <h3 className="text-lg font-semibold mb-1" style={{ color: '#1d1d1f' }}>WhatsApp</h3>
                <p className="text-sm mb-4" style={{ color: '#6e6e73' }}>Chat with us instantly on WhatsApp</p>
                <Button className="rounded-full w-full text-white hover:opacity-90" style={{ background: '#1d1d1f' }}>
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Message on WhatsApp
                </Button>
              </CardContent>
            </Card>
          </a>

          {/* SMS / Text Message */}
          <a 
            href="sms:+919717178733?body=Hi, I'd like to learn more about improving my emotional intelligence."
            data-testid="sms-contact-btn"
          >
            <Card className="rounded-2xl border-black/5 bg-white card-hover cursor-pointer h-full" style={{transition: 'transform 0.2s ease, box-shadow 0.2s ease'}}>
              <CardContent className="p-8 text-center">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(0,113,227,0.1)' }}>
                  <Phone className="h-7 w-7" style={{ color: '#0071e3' }} />
                </div>
                <h3 className="text-lg font-semibold mb-1" style={{ color: '#1d1d1f' }}>Text Message</h3>
                <p className="text-sm mb-4" style={{ color: '#6e6e73' }}>Send us a text and we'll respond quickly</p>
                <Button className="rounded-full w-full text-white hover:opacity-90" style={{ background: 'linear-gradient(135deg, #0071e3 0%, #005ec7 100%)' }}>
                  <Phone className="h-4 w-4 mr-2" />
                  Send a Text
                </Button>
              </CardContent>
            </Card>
          </a>
        </div>


        {/* Benefits */}
        <Card className="rounded-2xl border-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
          <CardContent className="p-8 md:p-10">
            <p className="text-xs font-bold uppercase tracking-widest text-white mb-3">Why connect with us?</p>
            <h2 className="text-2xl font-semibold tracking-tight mb-6 text-white" style={{ letterSpacing: '-0.02em' }}>Personalised support that changes lives</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Heart className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-sm">Relationship Coaching</p>
                  <p className="text-xs text-smoke">Build deeper, more meaningful connections</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Shield className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-sm">Stress Management</p>
                  <p className="text-xs text-smoke">Proven techniques to stay calm under pressure</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Brain className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-sm">Self-Awareness</p>
                  <p className="text-xs text-smoke">Understand your emotional patterns deeply</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Sparkles className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-sm">Career Growth</p>
                  <p className="text-xs text-smoke">90% of top performers have high EI</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Experts;
